import React, { useState, useEffect, useMemo, useRef } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  FiMessageSquare,
  FiSearch,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiSend,
  FiPhone,
  FiUser,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiCopy,
} from "react-icons/fi";
import { collection, query, orderBy, onSnapshot, limit } from "firebase/firestore";

const STATUS_CONFIG = {
  open: {
    label: "รอดำเนินการ",
    color: "bg-amber-100 text-amber-800 border-amber-300",
    badge: "bg-amber-500 text-white",
  },
  in_progress: {
    label: "กำลังดำเนินการ",
    color: "bg-sky-100 text-sky-800 border-sky-300",
    badge: "bg-sky-500 text-white",
  },
  resolved: {
    label: "แก้ไขเรียบร้อย",
    color: "bg-emerald-100 text-emerald-800 border-emerald-300",
    badge: "bg-emerald-600 text-white",
  },
  closed: {
    label: "ปิดเรื่องแล้ว",
    color: "bg-slate-100 text-slate-700 border-slate-300",
    badge: "bg-slate-500 text-white",
  },
};

const CATEGORIES = [
  "ทั้งหมด",
  "ปัญหาคำสั่งซื้อ / สลาก",
  "การชำระเงิน / สลิปโอนเงิน",
  "การรับเงินรางวัล",
  "ระบบขัดข้อง / แจ้งข้อผิดพลาด",
  "การบริการและเจ้าหน้าที่",
  "ข้อเสนอแนะ / อื่นๆ",
  "ทั่วไป",
];

export const Complains = () => {
  const { callApi, db } = Context();
  const { toast } = useToast();

  const [complains, setComplains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("ทั้งหมด");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const unsubRef = useRef(null);

  // 1. Attach Real-time listener to Complains collection
  useEffect(() => {
    setLoading(true);
    if (db) {
      try {
        const q = query(
          collection(db, "Complains"),
          orderBy("createdAt", "desc"),
          limit(200)
        );
        unsubRef.current = onSnapshot(
          q,
          (snapshot) => {
            const list = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                shortId: data.shortId || doc.id.slice(-5).toUpperCase(),
                status: data.status || "open",
                category: data.category || "ทั่วไป",
                notes: Array.isArray(data.notes) ? data.notes : [],
              };
            });
            setComplains(list);
            setLoading(false);
          },
          (err) => {
            console.warn("Complains listener error, fallback to API:", err);
            fetchComplainsViaApi();
          }
        );
      } catch (e) {
        console.warn("Failed to attach listener, using API:", e);
        fetchComplainsViaApi();
      }
    } else {
      fetchComplainsViaApi();
    }

    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [db]);

  const fetchComplainsViaApi = async () => {
    setLoading(true);
    try {
      const res = await callApi(
        "complains-getAll",
        { limit: 200, orderBy: ["createdAt", "desc"] },
        { notShowLoading: true }
      );
      if (Array.isArray(res)) {
        setComplains(
          res.map((item) => ({
            ...item,
            shortId: item.shortId || item.id?.slice(-5).toUpperCase(),
            status: item.status || "open",
            category: item.category || "ทั่วไป",
            notes: Array.isArray(item.notes) ? item.notes : [],
          }))
        );
      }
    } catch (error) {
      console.error("fetchComplainsViaApi error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 2. Metrics calculation
  const metrics = useMemo(() => {
    const total = complains.length;
    const open = complains.filter((c) => c.status === "open").length;
    const inProgress = complains.filter((c) => c.status === "in_progress").length;
    const resolved = complains.filter((c) => c.status === "resolved").length;
    const closed = complains.filter((c) => c.status === "closed").length;
    return { total, open, inProgress, resolved, closed };
  }, [complains]);

  // 3. Filtered & Searched Complains
  const filteredComplains = useMemo(() => {
    return complains.filter((item) => {
      // Status filter
      if (statusFilter !== "all" && item.status !== statusFilter) {
        return false;
      }
      // Category filter
      if (categoryFilter !== "ทั้งหมด" && item.category !== categoryFilter) {
        return false;
      }
      // Search query
      if (searchQuery.trim()) {
        const queryLower = searchQuery.toLowerCase().trim();
        const shortId = (item.shortId || "").toLowerCase();
        const fullName = `${item.firstName || ""} ${item.lastName || ""}`.toLowerCase();
        const phone = (item.phone || "").toLowerCase();
        const userId = String(item.userId || "").toLowerCase();
        const topic = (item.topic || "").toLowerCase();
        const title = (item.title || "").toLowerCase();

        return (
          shortId.includes(queryLower) ||
          fullName.includes(queryLower) ||
          phone.includes(queryLower) ||
          userId.includes(queryLower) ||
          topic.includes(queryLower) ||
          title.includes(queryLower)
        );
      }
      return true;
    });
  }, [complains, statusFilter, categoryFilter, searchQuery]);

  // 4. Pagination
  const totalPages = Math.ceil(filteredComplains.length / pageSize) || 1;
  const paginatedComplains = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredComplains.slice(start, start + pageSize);
  }, [filteredComplains, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, searchQuery]);

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto p-4 space-y-4">
      {/* Header */}
      <Header
        header="เรื่องร้องเรียน (Complains)"
        subHeader="ศูนย์จัดการข้อร้องเรียนและข้อเสนอแนะจากลูกค้าแบบ Real-time"
        icon={<FiMessageSquare className="text-navy" />}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {/* 1. ทั้งหมด */}
        <div
          onClick={() => setStatusFilter("all")}
          className={`p-3 bg-slate-900 text-white rounded-md shadow-sm flex flex-col justify-between cursor-pointer transition hover:opacity-90 ${
            statusFilter === "all" ? "ring-2 ring-slate-400" : ""
          }`}
        >
          <span className="text-xs font-bold text-slate-300 uppercase">เรื่องทั้งหมด</span>
          <p className="text-2xl font-black mt-1">{metrics.total}</p>
        </div>

        {/* 2. รอดำเนินการ (Open) */}
        <div
          onClick={() => setStatusFilter("open")}
          className={`p-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-md shadow-sm flex flex-col justify-between cursor-pointer transition hover:opacity-90 ${
            statusFilter === "open" ? "ring-2 ring-amber-300" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-100 uppercase">รอดำเนินการ</span>
            {metrics.open > 0 && (
              <span className="w-2 h-2 rounded-full bg-white animate-ping" />
            )}
          </div>
          <p className="text-2xl font-black mt-1">{metrics.open}</p>
        </div>

        {/* 3. กำลังดำเนินการ (In Progress) */}
        <div
          onClick={() => setStatusFilter("in_progress")}
          className={`p-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-md shadow-sm flex flex-col justify-between cursor-pointer transition hover:opacity-90 ${
            statusFilter === "in_progress" ? "ring-2 ring-sky-300" : ""
          }`}
        >
          <span className="text-xs font-bold text-sky-100 uppercase">กำลังดำเนินการ</span>
          <p className="text-2xl font-black mt-1">{metrics.inProgress}</p>
        </div>

        {/* 4. แก้ไขเรียบร้อย (Resolved) */}
        <div
          onClick={() => setStatusFilter("resolved")}
          className={`p-3 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-md shadow-sm flex flex-col justify-between cursor-pointer transition hover:opacity-90 ${
            statusFilter === "resolved" ? "ring-2 ring-emerald-300" : ""
          }`}
        >
          <span className="text-xs font-bold text-emerald-100 uppercase">แก้ไขเรียบร้อย</span>
          <p className="text-2xl font-black mt-1">{metrics.resolved}</p>
        </div>

        {/* 5. ปิดเรื่องแล้ว (Closed) */}
        <div
          onClick={() => setStatusFilter("closed")}
          className={`p-3 bg-gradient-to-r from-slate-600 to-gray-700 text-white rounded-md shadow-sm flex flex-col justify-between cursor-pointer transition hover:opacity-90 ${
            statusFilter === "closed" ? "ring-2 ring-slate-400" : ""
          }`}
        >
          <span className="text-xs font-bold text-slate-200 uppercase">ปิดเรื่องแล้ว</span>
          <p className="text-2xl font-black mt-1">{metrics.closed}</p>
        </div>
      </div>

      {/* Filter & Control Bar */}
      <div className="bg-white rounded-md p-3.5 shadow-sm border border-slate-200 flex flex-wrap items-center justify-between gap-3">
        {/* Left: Status Filter Tabs */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-md text-xs font-bold flex-wrap">
          {[
            { id: "all", label: `ทั้งหมด (${metrics.total})` },
            { id: "open", label: `รอดำเนินการ (${metrics.open})` },
            { id: "in_progress", label: `กำลังดำเนินการ (${metrics.inProgress})` },
            { id: "resolved", label: `แก้ไขแล้ว (${metrics.resolved})` },
            { id: "closed", label: `ปิดแล้ว (${metrics.closed})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1.5 rounded-md transition ${
                statusFilter === tab.id
                  ? "bg-white text-slate-900 shadow-xs font-bold"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Right: Category Dropdown & Search & Refresh */}
        <div className="flex items-center gap-2 flex-1 sm:flex-none justify-end">
          {/* Category Filter */}
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="text-xs font-semibold bg-slate-50 border border-slate-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === "ทั้งหมด" ? "ทุกหมวดหมู่" : cat}
                </option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div className="relative w-64">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="ค้นหา ID, ลูกค้า, เบอร์..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs pl-7 pr-3 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue bg-slate-50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <FiX className="text-xs" />
              </button>
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchComplainsViaApi}
            disabled={loading}
            className="p-1.5 text-slate-600 hover:text-navy hover:bg-slate-100 rounded-md border border-slate-200 transition"
            title="รีเฟรชข้อมูล"
          >
            <FiRefreshCw className={`text-sm ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
        {/* Table Top Bar */}
        <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-slate-800">
              รายการเรื่องร้องเรียน
            </span>
            <span className="text-xs font-semibold bg-slate-200 text-slate-700 px-2 py-0.5 rounded-md">
              {filteredComplains.length} รายการ
            </span>
          </div>

          {/* Pagination Navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1 text-xs font-semibold">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronLeft />
              </button>
              <span className="px-2 py-0.5 text-slate-600 font-mono">
                หน้า {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronRight />
              </button>
            </div>
          )}
        </div>

        {/* Complains Table */}
        <Table>
          <TableHeader className="bg-slate-100 border-b border-slate-200">
            <TableRow>
              <TableHead className="w-12 text-center text-xs font-bold text-slate-700 py-2">
                ลำดับ
              </TableHead>
              <TableHead className="w-28 text-center text-xs font-bold text-slate-700 py-2">
                วัน / เวลา
              </TableHead>
              <TableHead className="w-24 text-center text-xs font-bold text-slate-700 py-2">
                รหัสเรื่อง
              </TableHead>
              <TableHead className="w-48 text-xs font-bold text-slate-700 py-2">
                ข้อมูลลูกค้า
              </TableHead>
              <TableHead className="w-36 text-xs font-bold text-slate-700 py-2">
                หมวดหมู่
              </TableHead>
              <TableHead className="text-xs font-bold text-slate-700 py-2">
                รายละเอียดเรื่องร้องเรียน
              </TableHead>
              <TableHead className="w-36 text-center text-xs font-bold text-slate-700 py-2">
                สถานะ
              </TableHead>
              <TableHead className="w-80 text-xs font-bold text-slate-700 py-2">
                บันทึก & ตอบกลับ (Notes)
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && complains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs font-semibold">กำลังโหลดข้อมูลเรื่องร้องเรียน...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : paginatedComplains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                    <FiCheckCircle className="text-3xl text-emerald-500" />
                    <span className="text-sm font-semibold text-slate-600">
                      ไม่พบรายการเรื่องร้องเรียนที่ค้นหา
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedComplains.map((item, idx) => (
                <ComplainRow
                  key={item.id}
                  complain={item}
                  index={(currentPage - 1) * pageSize + idx + 1}
                  onStatusChange={fetchComplainsViaApi}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Subcomponent: Complain Table Row
const ComplainRow = ({ complain, index, onStatusChange }) => {
  const { callApi } = Context();
  const { toast } = useToast();

  const [noteInput, setNoteInput] = useState("");
  const [updating, setUpdating] = useState(false);

  const itemDate = complain.createdAt
    ? new Date(complain.createdAt).toLocaleDateString("th-TH", {
        year: "2-digit",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  // Handle Status Update
  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === complain.status) return;
    setUpdating(true);
    try {
      const res = await callApi(
        "complains-updateStatus",
        { id: complain.id, value: newStatus },
        { notShowLoading: true }
      );
      if (res?.status) {
        toast({
          title: "อัปเดตสถานะสำเร็จ",
          description: `เรื่อง #${complain.shortId} เปลี่ยนเป็น "${STATUS_CONFIG[newStatus]?.label}"`,
          className: "bg-emerald-600 text-white",
        });
        if (onStatusChange) onStatusChange();
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: err.message,
        className: "bg-rose-600 text-white",
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle Add Note
  const handleAddNote = async (e) => {
    if (e) e.preventDefault();
    if (!noteInput.trim()) return;

    setUpdating(true);
    try {
      const res = await callApi(
        "complains-updateNotes",
        { id: complain.id, value: noteInput.trim() },
        { notShowLoading: true }
      );
      if (res?.status) {
        setNoteInput("");
        toast({
          title: "บันทึกข้อมูลเรียบร้อย",
          description: `เพิ่มบันทึกลงใน #${complain.shortId}`,
          className: "bg-navy text-white",
        });
        if (onStatusChange) onStatusChange();
      }
    } catch (err) {
      console.error("Failed to add note:", err);
      toast({
        title: "ไม่สามารถเพิ่มบันทึกได้",
        description: err.message,
        className: "bg-rose-600 text-white",
      });
    } finally {
      setUpdating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({
      title: `คัดลอก ${label} แล้ว`,
      description: text,
      className: "bg-slate-800 text-white",
    });
  };

  return (
    <TableRow
      className={`border-b border-slate-100 hover:bg-slate-50/80 transition align-top ${
        complain.status === "open" ? "bg-amber-50/20" : ""
      }`}
    >
      {/* 1. ลำดับ */}
      <TableCell className="text-center font-mono text-xs text-slate-500 py-3">
        {index}
      </TableCell>

      {/* 2. วัน/เวลา */}
      <TableCell className="text-center text-xs py-3 whitespace-nowrap">
        <span className="font-mono font-semibold text-slate-700 block">
          {itemDate.split(" ")[0]}
        </span>
        <span className="text-[10px] text-slate-400">
          {itemDate.split(" ")[1]} น.
        </span>
      </TableCell>

      {/* 3. รหัสเรื่อง */}
      <TableCell className="text-center py-3">
        <button
          onClick={() => copyToClipboard(complain.shortId, "รหัสเรื่อง")}
          className="inline-flex items-center gap-1 font-mono font-bold text-xs bg-slate-100 text-navy hover:bg-slate-200 px-2 py-0.5 rounded-md transition"
          title="คลิกเพื่อคัดลอก"
        >
          <span>#{complain.shortId}</span>
          <FiCopy className="text-[10px] text-slate-400" />
        </button>
      </TableCell>

      {/* 4. ข้อมูลลูกค้า */}
      <TableCell className="py-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1 font-bold text-xs text-slate-800">
            <FiUser className="text-slate-400 text-[11px]" />
            <span>
              {complain.firstName || complain.lastName
                ? `${complain.firstName || ""} ${complain.lastName || ""}`.trim()
                : "ลูกค้า"}
            </span>
          </div>
          {complain.userId && (
            <span className="text-[10px] font-mono text-slate-500 block">
              UID: #{complain.userId}
            </span>
          )}
          {complain.phone && (
            <button
              onClick={() => copyToClipboard(complain.phone, "เบอร์โทร")}
              className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-blue hover:text-navy"
            >
              <FiPhone className="text-[10px]" />
              <span>{complain.phone}</span>
            </button>
          )}
        </div>
      </TableCell>

      {/* 5. หมวดหมู่ */}
      <TableCell className="py-3">
        <span className="inline-block text-[11px] font-bold text-navy bg-navy/10 px-2 py-0.5 rounded-md">
          {complain.category || "ทั่วไป"}
        </span>
      </TableCell>

      {/* 6. รายละเอียดเรื่องร้องเรียน */}
      <TableCell className="py-3 max-w-sm">
        {complain.title && complain.title !== complain.topic && (
          <h4 className="font-bold text-xs text-slate-900 mb-0.5">
            {complain.title}
          </h4>
        )}
        <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
          {complain.topic || complain.detail || "-"}
        </p>
      </TableCell>

      {/* 7. สถานะ & Selector */}
      <TableCell className="py-3 text-center">
        <div className="flex flex-col items-center gap-1.5">
          <select
            value={complain.status || "open"}
            disabled={updating}
            onChange={(e) => handleUpdateStatus(e.target.value)}
            className={`text-xs font-bold rounded-md px-2 py-1 border shadow-2xs cursor-pointer focus:outline-none transition ${
              STATUS_CONFIG[complain.status]?.color || "bg-slate-100 text-slate-700"
            }`}
          >
            <option value="open">🟡 รอดำเนินการ</option>
            <option value="in_progress">🔵 กำลังดำเนินการ</option>
            <option value="resolved">🟢 แก้ไขเรียบร้อย</option>
            <option value="closed">⚪ ปิดเรื่องแล้ว</option>
          </select>
        </div>
      </TableCell>

      {/* 8. บันทึก (Notes) & Response Box */}
      <TableCell className="py-3">
        <div className="space-y-2">
          {/* Notes History */}
          {Array.isArray(complain.notes) && complain.notes.length > 0 && (
            <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
              {complain.notes.map((n, nIdx) => (
                <div
                  key={nIdx}
                  className="bg-slate-50 border border-slate-200 rounded-md p-2 text-[11px]"
                >
                  <div className="flex justify-between items-center text-[10px] text-slate-400 mb-0.5">
                    <strong className="text-slate-700 font-semibold">{n.by || "Admin"}</strong>
                    <span>
                      {n.time
                        ? new Date(n.time).toLocaleTimeString("th-TH", {
                            hour: "2-digit",
                            minute: "2-digit",
                          }) + " น."
                        : ""}
                    </span>
                  </div>
                  <p className="text-slate-700 font-medium whitespace-pre-wrap break-words">
                    {n.note}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Add Note Input */}
          <form onSubmit={handleAddNote} className="flex items-center gap-1.5">
            <input
              type="text"
              placeholder="พิมพ์บันทึก / ตอบกลับ..."
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              className="flex-1 text-xs px-2.5 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue bg-white"
            />
            <button
              type="submit"
              disabled={updating || !noteInput.trim()}
              className="py-1.5 px-2.5 text-xs font-bold rounded-md bg-navy text-white hover:bg-blue transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 shrink-0"
              title="บันทึกข้อความ"
            >
              <FiSend className="text-[10px]" />
              <span>บันทึก</span>
            </button>
          </form>
        </div>
      </TableCell>
    </TableRow>
  );
};
export default Complains;
